import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import {
  Auth0OnboardingApi,
  Auth0Credentials,
  ApplicationCredentials,
  AppSummary,
  BackfillResult,
} from './types';

/**
 * Client implementation for the Auth0 Onboarding API
 */
export class Auth0OnboardingClient implements Auth0OnboardingApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return await this.discoveryApi.getBaseUrl('auth0-onboarding');
  }

  async getCredentials(
    appName: string,
    environment: string,
  ): Promise<Auth0Credentials> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/credentials/${encodeURIComponent(appName)}/${environment}`,
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error.message || `Failed to fetch credentials: ${response.statusText}`,
      );
    }

    return response.json();
  }

  async getApplicationCredentials(
    appName: string,
  ): Promise<ApplicationCredentials> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/credentials/${encodeURIComponent(appName)}`,
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error.message ||
          error.error ||
          `Failed to fetch credentials: ${response.statusText}`,
      );
    }

    return response.json();
  }

  async revealSecret(
    appName: string,
    environment: string,
  ): Promise<string> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/credentials/${encodeURIComponent(appName)}/${environment}/secret`,
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error.message || `Failed to reveal secret: ${response.statusText}`,
      );
    }

    const data = await response.json();
    return data.clientSecret;
  }

  async triggerOnboarding(
    appName: string,
  ): Promise<{ status: string; message: string }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/onboarding/trigger`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ appName }),
      },
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error.message || `Failed to trigger onboarding: ${response.statusText}`,
      );
    }

    return response.json();
  }

  async getArtifactUrl(appName: string): Promise<string | undefined> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/artifact/${encodeURIComponent(appName)}`,
    );
    if (response.status === 404) {
      return undefined;
    }
    if (!response.ok) {
      throw new Error(`Failed to get artifact URL (HTTP ${response.status})`);
    }
    const data = await response.json();
    return data.url;
  }

  async listApps(): Promise<AppSummary[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/apps`);
    if (!response.ok) {
      throw new Error(`Failed to list apps (HTTP ${response.status})`);
    }
    return response.json();
  }

  async getAppForm(
    appName: string,
  ): Promise<Record<string, unknown> | null> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/apps/${encodeURIComponent(appName)}/form`,
    );
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error(`Failed to load app form (HTTP ${response.status})`);
    }
    const data = await response.json();
    return data.form ?? null;
  }

  async backfillApps(): Promise<BackfillResult> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/apps/backfill`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || `Backfill failed (HTTP ${response.status})`);
    }
    return response.json();
  }
}
